import { BaseXHR } from '@utils/axios';
import { take, fork, select, call, put, takeLatest } from 'redux-saga/effects';
import { isServer } from '@utils/helper';
import {
  actions,
  CHECK_PROJECT_IS_VALID,
  GET_DETAIL_PROJECT,
  GET_INFO_USER,
} from './slice';
import { TENANT_DEFAULT } from './constants';
import { getOwnerPlan } from './api/getOwnerPlan';

export function* watchAndLog() {
  while (true) {
    try {
      const styleConsole =
        'background: yellow; font-weight: bold; color: #40a9ff8c;';
      const action = yield take('*');
      const state = yield select();
      if (action.type !== '@@router/LOCATION_CHANGE') {
        console.group(action.type);
        console.log('%cBefore State', styleConsole, state);
        console.info('%cDispatching', styleConsole, action);
        console.log('%cNext State', styleConsole, state);
        console.groupEnd();
      }
    } catch {}
  }
}

export function* checkProjectIsValid({ payload }: any) {
  try {
    yield call(BaseXHR.$get, '/project/checkPublic', {
      slug: payload.slug,
      host: payload.host,
    });
    yield put(CHECK_PROJECT_IS_VALID.success());
  } catch (err) {
    yield put(CHECK_PROJECT_IS_VALID.failure());
  }
}

const normalizeOfflineId = (value?: string) =>
  `${value || ''}`.replace(/\s+/g, '');

const resolveSelfHostProjectId = (payload?: { offlineId?: string }) => {
  const fromPayload = normalizeOfflineId(payload?.offlineId);
  if (fromPayload) return fromPayload;
  if (typeof window === 'undefined') return '';
  return normalizeOfflineId((window as any).offline_id);
};

export function* getDetailProject({ payload }: any) {
  const knownSlug =
    payload.id && !payload.domain && !payload.isPayment
      ? payload.id
      : undefined;
  const ownerPlanPromise = knownSlug
    ? getOwnerPlan({ slug: knownSlug }).catch(() => null)
    : null;
  const offlineId = resolveSelfHostProjectId(payload);

  try {
    let url = '';

    try {
      const { data } = yield call(
        BaseXHR.$get,
        '/tenant/get-by-project',
        {
          id: payload.host || payload.domain || payload.id,
        },
        offlineId ? { skipErrorNotify: true } : undefined,
      );
      yield put(actions.setTenant({ ...TENANT_DEFAULT, ...data.data }));
    } catch (err) {
      yield put(actions.setTenant(TENANT_DEFAULT));
    }

    if (payload.isPayment) url = `project/getPaymentPublic?id=${payload.id}`;
    else if (payload.isPublic)
      url = `project/getPublic?${
        payload.domain
          ? `domain=${payload.domain}`
          : `slug=${payload.id}&host=${payload.host}`
      }`;
    else url = `project/getPreview?slug=${payload.id}`;
    let response;
    try {
      response = payload.isPublic
        ? yield call(BaseXHR.$get, url, undefined, {
            skipErrorNotify: !!(payload.domain && offlineId),
          })
        : yield call(BaseXHR.$getWithAuth, url, {
            headers: {
              Authorization: `Bearer ${payload.token}`,
            },
          });
    } catch (err) {
      if (payload.domain && err?.response?.status === 404 && offlineId) {
        response = yield call(
          BaseXHR.$get,
          `project/getPaymentPublic?id=${offlineId}`,
        );
      } else {
        throw err;
      }
    }
    const data = response.data.data || response.data;
    yield put(GET_DETAIL_PROJECT.success(data));
    try {
      const slug = data?.slug || payload.id;
      if (!slug) {
        yield put(actions.setOwnerPlan(null));
      } else {
        const ownerPlan =
          ownerPlanPromise && slug === knownSlug
            ? yield call(() => ownerPlanPromise)
            : yield call(getOwnerPlan, { slug });
        yield put(actions.setOwnerPlan(ownerPlan));
      }
    } catch (ownerPlanErr) {
      yield put(actions.setOwnerPlan(null));
    }
  } catch (err) {
    console.error(
      `${err?.config?.url || ''}: ${
        err?.response?.status || err?.message || err
      }`,
    );
    yield put(GET_DETAIL_PROJECT.failure());
  }
}

export function* getInfoUser({ payload }: any) {
  try {
    const { data } = yield call(BaseXHR.$getWithAuth, '/account/profile', {
      headers: {
        Authorization: `Bearer ${payload.token}`,
      },
    });

    yield put(GET_INFO_USER.success(data.data));
  } catch (err) {
    yield put(GET_INFO_USER.failure());
  }
}

const homeSagas: any = [
  takeLatest(GET_INFO_USER.TRIGGER, getInfoUser),
  takeLatest(GET_DETAIL_PROJECT.TRIGGER, getDetailProject),
  takeLatest(CHECK_PROJECT_IS_VALID.TRIGGER, checkProjectIsValid),
];

if (!isServer && process.env.NODE_ENV === 'development')
  homeSagas.push(fork(watchAndLog));

export default homeSagas;
